import { type MouseEvent, useCallback, useEffect, useState } from 'react'
import { Spin } from 'antd'
import styles from '@/styles/productDetail/addOnPurchase.module.scss'
import AddOnSvg from '@/svgs/icon-add-on.svg'
import AddOnSuccess from "@/svgs/icon-add-on-success.svg"
import InfoSvg from '@/svgs/icon-Info.svg'
import { formatCurrency } from "@/utils/utils"
import Image from 'next/image'
import Link from 'next/link'
import AddOnDealsModal from './AddOnDealsModal'
import AddOnInfoModal from './productDetail/AddOnInfoModal'
import addOnApi from '@/service/modules/addOn'
import useCartActions from '@/utils/addTocartAction'
import useAddOnCartItems from '@/utils/useAddOnCartItems'

export interface AddOnProduct {
  productID?: number
  image: string
  name: string
  url?: string
  addonPrice: number
  originalPrice: number
  appSelectStatus?: number
}

interface AddOnPurchaseProps {
  onAddOnSuccess?: () => void
  refreshKey?: number | string
  refreshOnAddSuccess?: boolean
  subtotal?: number
  className?: string
  mainProduct?: {
    productID?: string | number
    qty: string | number
  }
}

const normalizeAddOnProduct = (item: any): AddOnProduct => ({
  productID: item.productID,
  image: item.filename || item.newThumbnail || item.thumbnail || item.image,
  name: item.name,
  url: item.url,
  addonPrice: Number(item.addonPrice),
  originalPrice: Number(item.normalPrice || item.originalPrice),
  appSelectStatus: item.appSelectStatus,
})

export default function AddOnPurchase({
  onAddOnSuccess,
  refreshKey,
  refreshOnAddSuccess = true,
  subtotal,
  className,
  mainProduct,
}: AddOnPurchaseProps) {
  const [showAddOnDealsModal, setShowAddOnDealsModal] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [selectedAddOns, setSelectedAddOns] = useState<AddOnProduct[]>([])
  const [selectedAddOnsLoading, setSelectedAddOnsLoading] = useState(false)
  const [selectedAddOnsLoaded, setSelectedAddOnsLoaded] = useState(false)
  const [addingProductIDs, setAddingProductIDs] = useState<Record<number, boolean>>({})
  const { addToCart, batchAddToCart } = useCartActions()
  const getAddOnCartItems = useAddOnCartItems(mainProduct)

  const getRedZoneAddOns = useCallback(async () => {
    try {
      setSelectedAddOnsLoading(true)
      setSelectedAddOnsLoaded(false)
      const params: any = { perPage: 1 }
      if (subtotal !== undefined) params.subtotal = subtotal
      const res = await addOnApi.getAddOnRedZone(params)
      const list = res?.data?.list || res?.data?.items || res?.data || []
      setSelectedAddOns(Array.isArray(list) ? list.map(normalizeAddOnProduct).filter((item) => item.productID && item.image) : [])
    } catch (error) {
      console.error("Add-on red zone data acquisition failed:", error)
      setSelectedAddOns([])
    } finally {
      setSelectedAddOnsLoading(false)
      setSelectedAddOnsLoaded(true)
    }
  }, [subtotal])

  const handleAddOnSuccess = () => {
    setShowAddOnDealsModal(false)
    if (refreshOnAddSuccess) {
      getRedZoneAddOns()
    }
    onAddOnSuccess?.()
  }

  const handleAddToCart = (event: MouseEvent, product: AddOnProduct) => {
    event.stopPropagation()
    if (!product.productID || addingProductIDs[product.productID]) return

    setAddingProductIDs((prev) => ({
      ...prev,
      [product.productID as number]: true,
    }))
    const productList = getAddOnCartItems(product.productID)
    const addAction = productList.length > 1
      ? batchAddToCart(
        event,
        { productList },
        handleAddToCartCallback(product),
      )
      : addToCart(
        event,
        { productID: product.productID, qty: 1, from: 1 },
        handleAddToCartCallback(product),
      )

    return addAction
  }

  const handleAddToCartCallback = (product: AddOnProduct) => (
    (response: any) => {
      setAddingProductIDs((prev) => {
        const next = { ...prev }
        delete next[product.productID as number]
        return next
      })
      if (response === "error") return
      setSelectedAddOns((prev) => prev.map((item) => (
        item.productID === product.productID
          ? { ...item, appSelectStatus: 1 }
          : item
      )))
      if (refreshOnAddSuccess) {
        getRedZoneAddOns()
      }
      onAddOnSuccess?.()
    }
  )

  const renderAddCartAction = (product: AddOnProduct) => {
    if (product.appSelectStatus) {
      return <AddOnSuccess className={styles['selected-icon']} />
    }

    if (product.productID && addingProductIDs[product.productID]) {
      return <Spin size="small" className={styles['cart-loading']} />
    }

    return (
      <AddOnSvg
        className={styles['cart-icon']}
        onClick={(event: MouseEvent) => handleAddToCart(event, product)}
      />
    )
  }

  useEffect(() => {
    getRedZoneAddOns()
  }, [getRedZoneAddOns, refreshKey])

  if (!selectedAddOnsLoaded && selectedAddOns.length === 0) {
    return null
  }

  if (selectedAddOnsLoaded && selectedAddOns.length === 0) {
    return null
  }

  return (
    <>
      <div className={`${styles['add-on-purchase']} ${className || ''}`}>
        {selectedAddOnsLoading && (
          <div className={styles['loading-mask']}>
            <Spin />
          </div>
        )}
        <div className={styles['add-on-header']}>
          <div className={styles['add-on-title']}>
            Super Value Add-on Deals
            <InfoSvg
              style={{ width: 18, marginLeft: 4 }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setShowInfoModal(true)
              }}
            />
          </div>
          <div className={styles['see-more']} onClick={() => setShowAddOnDealsModal(true)}>See More &gt;</div>
        </div>
        <div className={styles['add-on-list']}>
          {selectedAddOns.map((product, index) => (
            <div className={styles['add-on-item']} key={index}>
              <Link href={product.url || '#'} legacyBehavior>
                <a rel="noopener noreferrer" target="_blank" className={styles['add-on-img']}>
                  <Image
                    src={product.image}
                    alt={product.name}
                    width={132}
                    height={132}
                  />
                </a>
              </Link>
              <div className={styles['add-on-info']}>
                <Link href={product.url || '#'} legacyBehavior>
                  <a rel="noopener noreferrer" target="_blank" className={styles['add-on-name']}>
                    {product.name}
                  </a>
                </Link>
                <div className={styles['add-on-price']}>
                  <div className={styles['boxLeft']}>
                    <div className={styles['price-label']}>Add-on Price</div>
                    <span className={styles['current-price']}>{formatCurrency(product.addonPrice)}</span>
                    <span className={styles['original-price']}>{formatCurrency(product.originalPrice)}</span>
                  </div>
                  <div className={styles['boxRight']}>
                    {renderAddCartAction(product)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AddOnDealsModal
        show={showAddOnDealsModal}
        setShow={setShowAddOnDealsModal}
        onAddOnSuccess={handleAddOnSuccess}
        subtotal={subtotal}
        mainProduct={mainProduct}
      />
      <AddOnInfoModal open={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </>
  )
}
